import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Edit, Trash2, Upload, ChevronLeft, ChevronRight, Play, Pause, Volume2, X } from 'lucide-react';

import { soundEffectAPI, categoryAPI } from '../../services/api';
import CategoryCreateDialog from './CategoryCreateDialog';
import SoundEffectUploadDialog from './SoundEffectUploadDialog';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin, showAdminAccessDialog, ADMIN_ACTIONS } from '../../utils/adminUtils';

const SoundEffectsSidebar = ({ isOpen, onClose, onSoundEffectSelect, template_content, settemplate_content }) => {
  const { user } = useAuth();
  const [soundEffects, setSoundEffects] = useState([]);
  const [rootCategories, setRootCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRootCategory, setSelectedRootCategory] = useState('all');
  const [selectedChildCategory, setSelectedChildCategory] = useState('all');
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCategoryCreateDialog, setShowCategoryCreateDialog] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    tags: '',
    file: null
  });
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#3B82F6' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const audioRefs = useRef({});
  const rootCategoriesRef = useRef(null);
  const childCategoriesRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchSoundEffects();
      fetchRootCategories();
    }
  }, [isOpen, selectedRootCategory, selectedChildCategory, searchTerm]);

  // Fetch sound effects
  const fetchSoundEffects = async (resetSoundEffects = true, pageNum = 1) => {
    try {
      setLoading(resetSoundEffects);
      setLoadingMore(!resetSoundEffects);

      const params = {
        page: pageNum,
        limit: 20,
      };

      // Add category filter
      if (selectedChildCategory !== 'all') {
        params.category = selectedChildCategory;
      } else if (selectedRootCategory && selectedRootCategory !== 'all') {
        params.category = selectedRootCategory;
      }

      // Add search filter
      if (searchTerm) {
        params.search = searchTerm;
      }

      const resp = await soundEffectAPI.getAll(params);
      const data = resp.data;

      if (data.success) {
        const soundEffectsData = Array.isArray(data.data) ? data.data : data.data.soundEffects || [];

        if (resetSoundEffects) {
          setSoundEffects(soundEffectsData);
        } else {
          setSoundEffects(prev => [...prev, ...soundEffectsData]);
        }

        setHasMore(soundEffectsData.length === params.limit);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching sound effects:', error);
      setSoundEffects([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Fetch root categories (parentId is null)
  const fetchRootCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categoryAPI.getAll({ type: 'sound-effect', parentId: '' });
      const data = response.data;

      if (data.success) {
        const rootCategoriesData = data.data || [];
        // Sort root categories by name
        rootCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setRootCategories(rootCategoriesData);
        // Auto-select first root category if none selected
        if (!selectedRootCategory && rootCategoriesData.length > 0) {
          const firstRootId = rootCategoriesData[0].id;
          setSelectedRootCategory(firstRootId);
          fetchChildCategories(firstRootId);
        }
      }
    } catch (error) {
      console.error('Error fetching root categories:', error);
      setRootCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch child categories for selected root category
  const fetchChildCategories = async (parentId) => {
    try {
      if (!parentId || parentId === 'all') {
        setChildCategories([]);
        return;
      }

      const response = await categoryAPI.getAll({ type: 'sound-effect', parentId });
      const data = response.data;

      if (data.success) {
        const childCategoriesData = data.data || [];
        // Sort child categories by name
        childCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setChildCategories(childCategoriesData);
      }
    } catch (error) {
      console.error('Error fetching child categories:', error);
      setChildCategories([]);
    }
  };

  // Handle root category selection
  const handleRootCategorySelect = (category_id) => {
    setSelectedRootCategory(category_id);
    setSelectedChildCategory('all');
    setPage(1);

    if (category_id && category_id !== 'all') {
      fetchChildCategories(category_id);
    } else {
      setChildCategories([]);
    }
  };

  // Handle child category selection
  const handleChildCategorySelect = (category_id) => {
    setSelectedChildCategory(category_id);
    setPage(1);
  };

  // Horizontal scroll functions
  const scrollCategories = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 200;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Load more sound effects
  const loadMoreSoundEffects = () => {
    if (!loadingMore && hasMore) {
      fetchSoundEffects(false, page + 1);
    }
  };

  // Handle play/pause
  const handlePlay = async (soundEffectId) => {
    try {
      // Stop currently playing audio
      if (currentlyPlaying && audioRefs.current[currentlyPlaying]) {
        audioRefs.current[currentlyPlaying].pause();
        audioRefs.current[currentlyPlaying].currentTime = 0;
      }

      if (currentlyPlaying === soundEffectId) {
        setCurrentlyPlaying(null);
      } else {
        setCurrentlyPlaying(soundEffectId);
        const audioElement = audioRefs.current[soundEffectId];
        if (audioElement) {
          // Ensure audio is loaded before playing
          if (audioElement.readyState < 2) {
            console.log('🎵 Loading sound effect for playback...');
            await new Promise((resolve, reject) => {
              const onCanPlay = () => {
                audioElement.removeEventListener('canplay', onCanPlay);
                audioElement.removeEventListener('error', onError);
                resolve();
              };
              const onError = (e) => {
                audioElement.removeEventListener('canplay', onCanPlay);
                audioElement.removeEventListener('error', onError);
                console.error('❌ Error loading audio:', e);
                reject(e);
              };
              audioElement.addEventListener('canplay', onCanPlay);
              audioElement.addEventListener('error', onError);
              audioElement.load(); // Force load
              
              // Timeout after 10 seconds
              setTimeout(() => {
                audioElement.removeEventListener('canplay', onCanPlay);
                audioElement.removeEventListener('error', onError);
                reject(new Error('Audio loading timeout'));
              }, 10000);
            });
          }

          console.log('🎵 Playing sound effect:', soundEffectId);
          await audioElement.play().catch(error => {
            console.error('❌ Error playing audio:', error);
            // Handle autoplay policy errors
            if (error.name === 'NotAllowedError') {
              console.warn('⚠️ Autoplay blocked. User interaction required.');
            }
            setCurrentlyPlaying(null);
          });
        } else {
          console.error('❌ Audio element not found for ID:', soundEffectId);
        }
      }
    } catch (error) {
      console.error('❌ Error playing sound effect:', error);
      setCurrentlyPlaying(null);
    }
  };

  // Handle audio end
  const handleAudioEnd = (soundEffectId) => {
    if (currentlyPlaying === soundEffectId) {
      setCurrentlyPlaying(null);
    }
  };

  // Handle add sound effect to timeline
  const handleAddSoundEffect = (soundEffect) => {
    if (onSoundEffectSelect) {
      onSoundEffectSelect(soundEffect);
    }
  };

  // Create category
  const createCategory = async () => {
    // Check admin access
    if (!isAdmin(user)) {
      showAdminAccessDialog(ADMIN_ACTIONS.CREATE_CATEGORY);
      return;
    }

    try {
      await categoryAPI.create({ ...newCategory, type: 'sound-effect' });
      setNewCategory({ name: '', description: '', color: '#3B82F6' });
      fetchRootCategories();

      // Show success notification
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'success',
          message: 'Sound effects category created successfully!'
        }
      }));
    } catch (error) {
      console.error('Error creating category:', error);

      // Show error notification
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'error',
          message: error.response?.data?.message || 'Failed to create category. Please try again.'
        }
      }));
    }
  };

  // Update category
  const updateCategory = async (id, updates) => {
    try {
      await categoryAPI.update(id, updates);
      fetchRootCategories();
      setEditingCategory(null);
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  // Delete category
  const deleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await categoryAPI.delete(id);
        fetchRootCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Custom CSS for hiding scrollbars */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold">Sound Effects</h2>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowUploadModal(true)}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
              title="Upload Sound Effect"
            >
              <Upload className="w-5 h-5 text-gray-500" />
            </button>
            <button
              onClick={() => setShowCategoryManager(!showCategoryManager)}
              className="p-2 hover:bg-gray-100 rounded"
              title="Manage Categories"
            >
              <Edit className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search sound effects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Category Manager */}
          {showCategoryManager && (
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="font-medium mb-3">Manage Categories</h3>
              {/* Add New Category */}
              <div className="space-y-2 mb-4">
                <input
                  type="text"
                  placeholder="Category name"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={newCategory.color}
                    onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                    className="w-10 h-8 border border-gray-300 rounded"
                  />
                  <button
                    onClick={createCategory}
                    disabled={!newCategory.name}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4 inline mr-1" />
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* First Line - Root Categories */}
          <div className="px-4 py-3">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => scrollCategories(rootCategoriesRef, 'left')}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                title="Scroll left"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <div
                ref={rootCategoriesRef}
                className="flex space-x-2 overflow-x-auto scrollbar-hide flex-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <button
                  onClick={() => handleRootCategorySelect('all')}
                  className={`px-2 py-1 rounded-full text-xs whitespace-nowrap flex-shrink-0 transition-colors ${
                    selectedRootCategory === 'all' || !selectedRootCategory
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  All Categories
                </button>
                {rootCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleRootCategorySelect(category.id)}
                    className={`px-2 py-1 rounded-full text-xs whitespace-nowrap flex-shrink-0 flex items-center space-x-1 transition-colors ${
                      selectedRootCategory === category.id
                        ? 'text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                    style={{
                      backgroundColor: selectedRootCategory === category.id ? category.color || '#9333EA' : undefined,
                    }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        backgroundColor: selectedRootCategory === category.id
                          ? 'white'
                          : category.color || '#9333EA'
                      }}
                    />
                    <span>{category.name}</span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => scrollCategories(rootCategoriesRef, 'right')}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                title="Scroll right"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Second Line - Child Categories */}
          <div className="px-4 pb-3">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => scrollCategories(childCategoriesRef, 'left')}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                title="Scroll left"
              >
                <ChevronLeft className="w-3 h-3 text-gray-500" />
              </button>
              <div
                ref={childCategoriesRef}
                className="flex space-x-2 overflow-x-auto scrollbar-hide flex-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <button
                  onClick={() => handleChildCategorySelect('all')}
                  className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap flex-shrink-0 transition-colors ${
                    selectedChildCategory === 'all'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  All
                </button>
                {childCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleChildCategorySelect(category.id)}
                    className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap flex-shrink-0 flex items-center space-x-1 transition-colors ${
                      selectedChildCategory === category.id
                        ? 'text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                    style={{
                      backgroundColor: selectedChildCategory === category.id ? category.color || '#9333EA' : undefined,
                    }}
                  >
                    <div
                      className="w-1 h-1 rounded-full"
                      style={{
                        backgroundColor: selectedChildCategory === category.id
                          ? 'white'
                          : category.color || '#9333EA'
                      }}
                    />
                    <span>{category.name}</span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => scrollCategories(childCategoriesRef, 'right')}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                title="Scroll right"
              >
                <ChevronRight className="w-3 h-3 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Sound Effects List */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
              </div>
            ) : soundEffects.length > 0 ? (
              <div className="space-y-3">
                {soundEffects.map((soundEffect) => (
                  <div key={soundEffect.id} className="bg-gray-50 rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-3">
                      {/* Play Button */}
                      <button
                        onClick={() => handlePlay(soundEffect.id)}
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          currentlyPlaying === soundEffect.id
                            ? 'bg-purple-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                        }`}
                      >
                        {currentlyPlaying === soundEffect.id ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4 ml-0.5" />
                        )}
                      </button>

                      {/* Sound Effect Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {soundEffect.filename || soundEffect.originalName || soundEffect.name || 'Untitled'}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {soundEffect.duration ? `${Math.round(soundEffect.duration)}s` : 'Unknown duration'}
                          {soundEffect.category && ` • ${soundEffect.category}`}
                        </p>
                      </div>

                      {/* Add Button */}
                      <button
                        onClick={() => handleAddSoundEffect(soundEffect)}
                        className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>

                    {/* Hidden Audio Element */}
                    <audio
                      ref={(el) => {
                        if (el) {
                          audioRefs.current[soundEffect.id] = el;
                          // Set crossOrigin for CORS if needed
                          const url = soundEffect.storageUrl || soundEffect.url || soundEffect.storage_url;
                          if (url && url.startsWith('http')) {
                            el.crossOrigin = 'anonymous';
                          }
                        }
                      }}
                      onEnded={() => handleAudioEnd(soundEffect.id)}
                      onError={(e) => {
                        console.error('❌ Audio playback error for:', soundEffect.id, e);
                        setCurrentlyPlaying(null);
                      }}
                      preload="none"
                      crossOrigin="anonymous"
                    >
                      <source src={soundEffect.storageUrl || soundEffect.url || soundEffect.storage_url} type={soundEffect.mimeType || soundEffect.mime_type || 'audio/mpeg'} />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                ))}

                {/* Load More Button */}
                {hasMore && (
                  <div className="text-center pt-4">
                    <button
                      onClick={loadMoreSoundEffects}
                      disabled={loadingMore}
                      className="px-4 py-2 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 transition-colors"
                    >
                      {loadingMore ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12">
                <Volume2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No sound effects found</p>
                {searchTerm && (
                  <p className="text-sm mt-1">Try adjusting your search or category filter</p>
                )}
                {selectedChildCategory !== 'all' && (
                  <p className="text-sm mt-1">Try selecting a different category</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sound Effect Upload Dialog */}
      <SoundEffectUploadDialog
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={() => {
          setShowUploadModal(false);
          fetchSoundEffects(); // Refresh the list
        }}
      />
    </>
  );
};

export default SoundEffectsSidebar;
